import { useEffect, useState } from "react";
import AppLoader from "@/app/components/webloading/loader/AppLoader";
import SuccessModalBulkUpload from "@/app/models/dialogs/SuccessModalBulkUpload";
import { useNavigate } from "react-router-dom";
import {
  gxBulkFilesRetryApi,
  GxNonceApi,
} from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import type { PaymentsBulkApprovalViewSummaryResponse } from "@/app/types/bulkPaymentsPayroll";
import DialogInviteUsers from "@/app/models/dialogs/DialogInviteUsers";
import BulkUploadIndividualDetails from "../../bulk-uploads/bulk-uploadApprove/BulkUploadIndividualDetails";
import { generatePkcePair, setCookie } from "@/app/services/Login/pkceService";
import type { VerifierAndChallenge } from "@/app/types/payments";
import { setBulkCbuaeContext } from "@/app/utils/helpers";
import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
import type { BulkFileRow } from "../../bulk-uploads/BulkUpload";
import { t } from "i18next";
import { NONCE_TYPE } from "../../bulk-uploads/nonceTypes";

interface RetryPayrollProp {
  isOpenBulkList: boolean;
  setIsOpenBulkList: (value: boolean) => void;
  selectedRow: BulkFileRow;
  bulkType: "PAYMENT" | "PAYROLL";
  access: { view: boolean; approve: boolean };
  setCallCount: React.Dispatch<React.SetStateAction<boolean>>;
}

const RetryPayroll = ({
  isOpenBulkList,
  setIsOpenBulkList,
  selectedRow,
  bulkType,
  access,
  setCallCount,
}: RetryPayrollProp) => {
  const navigate = useNavigate();
  const { isCB } = useAppRoutingType();

  const [isLoading, setIsLoading] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<any>();

  const [verifierAndChallenge, setVerifierAndChallenge] =
    useState<VerifierAndChallenge>({ verifier: "", challenge: "" });

  // === Fetch nonce ===========================================================
  const fetchNonce = async (): Promise<string> => {
    try {
      const result = await GxNonceApi({ type: NONCE_TYPE.RETRY });
      if (result?.status === 1 && result?.data?.nonceId) {
        return result.data.nonceId;
      }
    } catch {}
    return "";
  };

  const handleCloseEditModal = () => {
    setIsOpenBulkList(false);
  };

  // === Re-Submit handler =====================================================
  // Same PKCE + nonce + CBUAE pattern as BulkUploadApprove
  const handleReSubmit = async () => {
    if (!selectedRow?.gxJobProcessid) return;

    const currentNonceId = await fetchNonce();

    setIsLoading(true);
    try {
      const payload = {
        gxJobProcessId: selectedRow.gxJobProcessid,
        nonceId: currentNonceId,
        codeChallenge: isCB
          ? verifierAndChallenge.challenge || undefined
          : undefined,
        appUrl: isCB
          ? `${globalThis.location.origin}/bulk-uploads/cbuae/CbuaeSuccess`
          : null,
      };

      const result = await gxBulkFilesRetryApi(payload);

      if (result?.status === 1) {
        const responseData = result.data as any;
        const requestUri: string | null =
          responseData?.requestUri ?? responseData?.authorizationUrl ?? null;

        const bankName =
          responseData?.bankName ?? selectedRow?.jobStatusDesc ?? "--";

        if (isCB && requestUri) {
          if (verifierAndChallenge.verifier) {
            setCookie(verifierAndChallenge.verifier);
          }

          setBulkCbuaeContext({
            gxJobProcessid: selectedRow.gxJobProcessid,
            bulkType,
            bankName,
            source: "bulkUploadApprove",
          });

          globalThis.location.href = requestUri;
          return;
        }

        if (isCB && !requestUri) {
          navigate("/bulk-uploads/cbuae/CbuaeSuccess", {
            state: {
              gxJobProcessid: selectedRow.gxJobProcessid,
              bulkType,
              bankName,
              source: "bulkUploadApprove",
            },
          });
          setCallCount(true);
          return;
        }

        setSuccessData({ aprdStatus: "A" });
        setOpenSuccessModal(true);
        setCallCount(true);
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const closeSuccessModal = () => {
    setOpenSuccessModal(false);
    setSuccessData(undefined);
    setIsOpenBulkList(false);
  };

  // === Mount: generate PKCE pair ============================================
  useEffect(() => {
    const initPkce = async () => {
      const { verifier, challenge } = await generatePkcePair();
      setVerifierAndChallenge({ verifier, challenge });
    };
    initPkce();
  }, []);

  const bulkDataForDetails =
    selectedRow as unknown as PaymentsBulkApprovalViewSummaryResponse;

  return (
    <>
      {isLoading && <AppLoader />}

      {isOpenBulkList && (
        <DialogInviteUsers
          isOpen={isOpenBulkList}
          handleClose={handleCloseEditModal}
          title={t("repair")}
          subtitle={t("transaction")}
          label={t("back_")}
          label1=""
          submit="Re-Submit"
          onDeleteClick={handleCloseEditModal}
          onResendClick={handleCloseEditModal}
          handleFooterSubmit={handleReSubmit}
          isShowBtn={access.approve}
          HorizontalLine={true}
        >
          <div className="gx-approve-required-modal relative">
            <div className="mt-3">
              <BulkUploadIndividualDetails
                bulkData={bulkDataForDetails}
                bulkType={bulkType}
                isApprovalMode={true}
              />
            </div>
          </div>
        </DialogInviteUsers>
      )}

      {openSuccessModal && successData && (
        <SuccessModalBulkUpload
          onClose={closeSuccessModal}
          isOpen={openSuccessModal}
          successData={{ aprdStatus: "A" }}
          successMessage="Re-Submitted"
          onNavigate={() => navigate("/bulk-uploads/managebulkfile")}
        />
      )}
    </>
  );
};

export default RetryPayroll;
